import { defaultResponseMessageObj } from "../../types/ApiResults";
import { Fields } from "../../types/SchemaFactory";
import { PaginationEnum } from "../../domain/enums/common/PaginationEnum";
import { schemaFactory } from "../../validation-schemas/SchemaFactory";
import LoadingPanel from "../../custom-components/CustomLoader";
import Pagination from "../../custom-components/CustomPagination";
import Toast from "../../custom-components/Toast";
import GenericEntityPageHeader from "./GenericEntityPageHeader";
import { useEntityManager } from "../../custom-hooks/useEntityManager";
import { EntityService } from "../../types/EntityService";
import { Langs } from "../../domain/enums/common/LangsEnum";

interface GenericEntityPageProps<T extends { id?: string; translations?: any[] }, F> {
    service: EntityService<T, F>;
    filtersFieldsMeta: Fields<F>;
    filtersSchema: ReturnType<typeof schemaFactory>;
    renderItem: (item: T, onEdit: (id: string) => void, onDelete: (id: string) => void) => JSX.Element;
    renderEditor: (props: {
        entity?: T;
        onClose: () => void;
        onSave: (saved: T) => void;
        translateFields: (
            translationFields: Record<string, string>,
            sourceLang: Langs,
            targetLang: Langs
        ) => Promise<Record<string, string>>;
    }) => JSX.Element;
    defaultLimit?: number;
}

export function GenericEntityPage<T extends { id?: string; translations?: any[] }, F>({
    service,
    filtersFieldsMeta,
    filtersSchema,
    renderItem,
    renderEditor,
    defaultLimit = PaginationEnum.SHOW_20,
}: GenericEntityPageProps<T, F>) {
    const {
        collection,
        page,
        filters,
        loading,
        editing,
        isEditorVisible,
        messageObj,
        setPage,
        setLimit,
        setFilters,
        setEditorVisible,
        setEditing,
        setMessageObj,
        openEditor,
        handleDelete,
        handleSave,
        translateFields,
    } = useEntityManager<T, F>({
        service,
        defaultLimit,
    });

    return (
        <div className="relative z-0">
            <GenericEntityPageHeader
                filtersFieldsMeta={filtersFieldsMeta}
                filters={filters}
                filtersSchema={filtersSchema}
                setFilters={setFilters}
                setLimit={setLimit}
                openEditor={openEditor}
            />

            <Pagination currentPage={page} totalPages={collection.totalPages} pageHandler={(val) => setPage(val)} />

            <div className="w-[95%] mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6 py-6">
                {loading ? (
                    <LoadingPanel isVisible={loading} />
                ) : (
                    <>
                        {collection.rows.map((item) => (
                            <div key={item.id}>
                                {renderItem(
                                    item,
                                    (id) => openEditor(id),
                                    (id) => handleDelete(id)
                                )}
                            </div>
                        ))}
                    </>
                )}

                {/* editor modal */}
                {isEditorVisible ? (
                    <div className="fixed bg-black/40 flex items-center justify-center p-4">
                        <div className="w-full max-w-4xl">
                            {renderEditor({
                                entity: editing,
                                onClose: () => {
                                    setEditing(null);
                                    setEditorVisible(false);
                                },
                                onSave: (saved) => {
                                    handleSave(saved as T);
                                    setEditing(null);
                                    setEditorVisible(false);
                                },
                                translateFields: (translationFields, sourceLang, targetLang) => {
                                    return translateFields(translationFields, sourceLang, targetLang);
                                },
                            })}
                        </div>
                    </div>
                ) : null}
            </div>

            <Toast messageObj={messageObj} resetMessageObj={() => setMessageObj(defaultResponseMessageObj)} />
        </div>
    );
}
